import en from './en';
import type { AdminUiCopy } from './types';

type ExtendedSections = Pick<
  AdminUiCopy,
  | 'ownerStaff'
  | 'ownerBlogManager'
  | 'ownerBlogEditor'
  | 'superAdminDashboard'
  | 'superAdminSecurity'
  | 'superAdminUsers'
  | 'superAdminDemoRequests'
  | 'superAdminUserDetail'
>;

const nlExtendedSections: ExtendedSections = {
  ownerStaff: {
    ownerTools: 'Owner-tools',
    notAuthorized: 'Niet geautoriseerd',
    restrictedBody:
      'Deze zone is alleen voor platform-eigenaars. Owner-tools beheren staff-toegang en schrijven direct naar productiepermissies.',
    ownerAccessRequired: 'Owner-toegang vereist',
    notInOwnerEmails: 'Je e-mail {email} staat niet in PLATFORM_OWNER_EMAILS.',
    addAndReauth:
      'Voeg deze toe en meld je opnieuw aan via Google/GitHub SSO. In productie wordt owner-status bepaald vanuit omgevingsconfiguratie en de SSO-sessie.',
    manageStaffBody:
      'Beheer staff-toegang (super admin). Wijzigingen gelden direct en worden gelogd voor audit. Gebruik Super Admin-tools om klantaccounts te schorsen of te heractiveren.',
    grantRevokeTitle: 'Staff-toegang geven/intrekken',
    email: 'E-mail',
    action: 'Actie',
    reason: 'Reden',
    grantStaff: 'Staff geven',
    revokeStaff: 'Staff intrekken',
    working: 'Bezig...',
    enterEmail: 'Vul een e-mail in om door te gaan',
    provideReason: 'Geef een reden op (voor auditlogs)',
    currentStaff: 'Huidige staff',
    noStaff: 'Geen staff-accounts.',
    tableEmail: 'E-mail',
    tableId: 'ID',
    tableActions: 'Acties',
    revoke: 'Intrekken',
    prev: 'Vorige',
    next: 'Volgende',
    pageOf: 'Pagina {page} van {total}',
    recentActions: 'Recente owner-acties',
    noRecentActions: 'Geen recente acties.',
    grantDialogTitle: 'Staff-toegang geven?',
    revokeDialogTitle: 'Staff-toegang intrekken?',
    grantDialogDescription: 'Dit geeft volledige platformtoegang aan {email}.',
    revokeDialogDescription: 'Dit verwijdert platformtoegang voor {email}.',
    confirmGrant: 'Geven',
    confirmRevoke: 'Intrekken',
    grantSuccess: 'Staff-toegang gegeven aan {email}',
    revokeSuccess: 'Staff-toegang ingetrokken voor {email}',
    failedUpdateStaff: 'Staff kon niet worden bijgewerkt',
    failedRevoke: 'Intrekken mislukt',
    revokeReasonPlaceholder: 'Reden voor intrekken',
    revokeReasonHint: 'Voor auditlogs; houd het kort.',
    ownerManagedUpdateDefault: 'Staff-update beheerd door eigenaar',
    accessNoLongerRequiredDefault: 'Toegang niet meer nodig',
  },
  ownerBlogManager: {
    title: 'Blogbeheer',
    subtitle: 'Blogposts maken en beheren',
    accessRestricted: 'Toegang beperkt',
    accessBody: 'Blogbeheer vereist owner- of super admin-toegang.',
    newPost: 'Nieuwe post',
    all: 'Alles',
    draft: 'Concept',
    published: 'Gepubliceerd',
    loadingPosts: 'Posts laden...',
    noPosts: 'Nog geen posts. Maak je eerste blogpost.',
    createFirstPost: 'Maak je eerste post om updates te publiceren.',
    createPost: 'Post maken',
    tableTitle: 'Titel',
    tableStatus: 'Status',
    tableAuthor: 'Auteur',
    tableDate: 'Datum',
    tableActions: 'Acties',
    edit: 'Bewerken',
    view: 'Bekijken',
    delete: 'Verwijderen',
    previous: 'Vorige',
    next: 'Volgende',
    pageOf: 'Pagina {page} van {total}',
    deleteTitle: 'Post verwijderen?',
    deleteBody: 'Deze actie kan niet ongedaan worden gemaakt. De post wordt definitief verwijderd.',
    cancel: 'Annuleren',
    confirmDelete: 'Verwijderen',
    toastPostDeleted: 'Post verwijderd',
    toastDeleteFailed: 'Post verwijderen mislukt',
  },
  ownerBlogEditor: {
    title: 'Blogeditor',
    accessRestricted: 'Toegang beperkt',
    accessBody: 'Blogbeheer vereist owner- of super admin-toegang.',
    editPost: 'Post bewerken',
    newPost: 'Nieuwe post',
    backToManager: 'Terug naar blogbeheer',
    saveDraft: 'Concept opslaan',
    publish: 'Publiceren',
    saving: 'Opslaan...',
    publishing: 'Publiceren...',
    postTitlePlaceholder: 'Posttitel',
    status: 'Status',
    draft: 'Concept',
    published: 'Gepubliceerd',
    slug: 'URL-slug',
    slugPlaceholder: 'post-slug',
    excerpt: 'Samenvatting',
    excerptPlaceholder: 'Korte samenvatting voor de bloglijst...',
    featuredImageUrl: 'URL uitgelichte afbeelding',
    tags: 'Tags',
    tagsPlaceholder: 'EUDI, Productupdate, Engineering',
    commaSeparated: 'Komma-gescheiden',
    authorName: 'Naam auteur',
    authorNamePlaceholder: 'Naam auteur',
    authorAvatarUrl: 'URL avatar auteur',
    remove: 'Verwijderen',
    importMarkdown: 'Markdown importeren',
    markdown: 'Markdown',
    markdownPlaceholder: 'Plak hier je markdown...',
    preview: 'Voorbeeld',
    previewPlaceholder: 'Voorbeeld verschijnt hier...',
    cancel: 'Annuleren',
    import: 'Importeren',
    titleRequired: 'Titel is verplicht',
    contentRequired: 'Inhoud is verplicht',
    failedLoadPost: 'Post laden mislukt',
    recoveredDraft: 'Niet-opgeslagen concept hersteld',
    postPublished: 'Post gepubliceerd',
    draftSaved: 'Concept opgeslagen',
    draftCreated: 'Concept gemaakt',
    failedSavePost: 'Post opslaan mislukt',
    imageUploaded: 'Afbeelding geüpload',
    avatarUploaded: 'Avatar geüpload',
    uploadFailed: 'Upload mislukt',
    markdownImported: 'Markdown geïmporteerd',
    startWritingPlaceholder: 'Begin met schrijven van je blogpost...',
  },
  superAdminDashboard: {
    loadingMetrics: 'Platformstatistieken laden...',
    platformOverview: 'Platformoverzicht',
    globalMetrics: 'Globale statistieken en performance-analyse',
    noMetrics: 'Geen statistieken beschikbaar',
    quickActions: 'Snelle acties',
    platformSecurity: 'Platformbeveiliging',
    viewAllUsers: 'Alle gebruikers bekijken',
    demoRequests: 'Demo-aanvragen',
    trialUsers: 'Trial-gebruikers',
    suspendedUsers: 'Geschorste gebruikers',
    userMetrics: 'Gebruikersstatistieken',
    totalUsers: 'Totaal gebruikers',
    activeUsers: 'Actieve gebruikers',
    activePaidUsers: 'Actieve betalende gebruikers',
    mrr: 'MRR',
    monthlyRecurringRevenue: 'Maandelijkse terugkerende omzet',
    ofTotal: '{value}% van totaal',
    ofActiveUsers: '{value}% van actieve gebruikers',
    planDistribution: 'Planverdeling',
    trial: 'Trial',
    pro: 'Pro',
    business: 'Business',
    activeUsersWithTotal: 'Actieve gebruikers{totalSuffix}',
    verificationSessions: 'Verificatiesessies',
    totalSessions: 'Totaal sessies',
    thisMonth: 'Deze maand',
    lastMonth: 'Vorige maand',
    growth: 'Groei',
    liveVerifications: 'Live-verificaties',
    allTime: 'Totaal',
    apiKeys: 'API-sleutels',
    totalKeys: 'Totaal sleutels',
    activeKeys: 'Actieve sleutels',
    liveKeys: 'Live-sleutels',
    productionEnvironment: 'Productieomgeving',
  },
  superAdminSecurity: {
    superAdmin: 'Super Admin',
    platformSecurity: 'Platformbeveiliging',
    subtitle: 'Cross-merchant anomalieoverzicht voor staff-accounts.',
    totalAlerts: 'Totaal alerts',
    activeAlerts: 'Actieve alerts',
    critical: 'Kritiek',
    topMerchantsLast7d: 'Top merchants (7 d)',
    filters: 'Filters',
    severity: 'Ernst',
    status: 'Status',
    merchantIdOptional: 'Merchant-ID (optioneel)',
    show: 'Tonen',
    any: 'Alles',
    low: 'Laag',
    medium: 'Middel',
    high: 'Hoog',
    active: 'Actief',
    investigating: 'In onderzoek',
    resolved: 'Opgelost',
    falsePositive: 'Vals positief',
    refresh: 'Vernieuwen',
    securityAlerts: 'Beveiligingsalerts',
    noAlerts: 'Geen alerts voor de huidige filters.',
    time: 'Tijd',
    merchant: 'Merchant',
    type: 'Type',
    pageOf: 'Pagina {page} van {total}',
    prev: 'Vorige',
    next: 'Volgende',
  },
  superAdminUsers: {
    superAdmin: 'Super Admin',
    allUsers: 'Alle gebruikers',
    subtitle: 'Gebruikersbeheer en analytics voor het hele platform',
    totalUsers: 'Totaal gebruikers',
    activeUsers: 'Actieve gebruikers',
    trialUsers: 'Trial-gebruikers',
    paidUsers: 'Betalende gebruikers',
    filters: 'Filters',
    exporting: 'Exporteren...',
    exportCsv: 'CSV exporteren',
    hideFilters: 'Filters verbergen',
    showFilters: 'Filters tonen',
    search: 'Zoeken',
    searchPlaceholder: 'E-mail, naam of bedrijf...',
    planTier: 'Planniveau',
    allTiers: 'Alle niveaus',
    trial: 'Trial',
    pro: 'Pro',
    business: 'Business',
    status: 'Status',
    allStatuses: 'Alle statussen',
    active: 'Actief',
    suspended: 'Geschorst',
    loadingUsers: 'Gebruikers laden...',
    noUsersFound: 'Geen gebruikers gevonden',
    tableUser: 'Gebruiker',
    tablePlan: 'Plan',
    tablePlanActive: 'Plan actief',
    tableUsage: 'Gebruik',
    tableResources: 'Resources',
    tableCreated: 'Aangemaakt',
    tableActions: 'Acties',
    inactive: 'Inactief',
    admin: 'Admin',
    liveThisMonth: 'live deze maand',
    keys: 'sleutels',
    sessions: 'sessies',
    updated: 'Bijgewerkt',
    viewSelf: 'Mijn account bekijken',
    viewDetails: 'Details bekijken',
    usage: 'Gebruik',
    resources: 'Resources',
    created: 'Aangemaakt',
    showingRange: '{start} t/m {end} van {total} gebruikers',
    previous: 'Vorige',
    next: 'Volgende',
    exportStarted: 'CSV-export gestart.',
    unableExport: 'Gebruikers exporteren mislukt.',
  },
  superAdminDemoRequests: {
    superAdmin: 'Super Admin',
    demoRequests: 'Demo-aanvragen',
    subtitle: 'Volg en beheer demo-aanvragen van prospects',
    totalRequests: 'Totaal aanvragen',
    pending: 'Open',
    contacted: 'Gecontacteerd',
    completed: 'Voltooid',
    allRequests: 'Alle aanvragen',
    pendingOnly: 'Alleen open',
    scheduled: 'Gepland',
    onboarded: 'Onboarded',
    followUp: 'Opvolging',
    churned: 'Afgehaakt',
    showing: '{count} van {total} aanvragen',
    loading: 'Demo-aanvragen laden...',
    noRequestsFound: 'Geen demo-aanvragen gevonden',
    noRequestsAll:
      'Er zijn nog geen demo-aanvragen ingestuurd. Ze verschijnen hier zodra klanten een demo boeken vanaf je landingspagina.',
    noRequestsWithStatus: 'Geen aanvragen met status "{status}". Probeer een ander filter.',
    contact: 'Contact',
    quickInfo: 'Snelle info',
    status: 'Status',
    submitted: 'Ingediend',
    actions: 'Acties',
    hideDetails: 'Details verbergen',
    showDetails: 'Details tonen',
    noInfo: 'Geen info',
    email: 'E-mail',
    role: 'Rol',
    useCase: 'Use case',
    expectedVolume: 'Verwacht volume',
    message: 'Bericht',
    viewFullDetails: 'Alle details bekijken',
    pageOf: 'Pagina {page} van {total}',
    justNow: 'Zojuist',
    minutesAgo: '{count} min geleden',
    hoursAgo: '{count} u geleden',
    daysAgo: '{count} d geleden',
    statusUpdated: 'Status succesvol bijgewerkt',
    statusUpdateFailed: 'Status bijwerken mislukt',
  },
  superAdminUserDetail: {
    loadingUserDetails: 'Gebruikersdetails laden...',
    backToUsers: 'Terug naar gebruikers',
    userNotFound: 'Gebruiker niet gevonden',
    superAdmin: 'Super Admin',
    allUsers: 'Alle gebruikers',
    editUser: 'Gebruiker bewerken',
    accountActions: 'Accountacties',
    accountActionsBody:
      'Geschorste accounts kunnen niet inloggen of nieuwe verificatiesessies starten.',
    suspending: 'Schorsen...',
    suspendAccount: 'Account schorsen',
    reactivating: 'Heractiveren...',
    reactivateAccount: 'Account heractiveren',
    updateUser: 'Gebruiker bijwerken',
    planTier: 'Planniveau',
    status: 'Status',
    free: 'Gratis',
    pro: 'Pro',
    business: 'Business',
    suspended: 'Geschorst',
    saveChanges: 'Wijzigingen opslaan',
    saving: 'Opslaan...',
    accountInformation: 'Accountinformatie',
    email: 'E-mail',
    name: 'Naam',
    company: 'Bedrijf',
    emailVerified: 'E-mail geverifieerd',
    verified: 'Geverifieerd',
    notVerified: 'Niet geverifieerd',
    authProvider: 'Auth-provider',
    created: 'Aangemaakt',
    lastUpdated: 'Laatst bijgewerkt',
    planDetails: 'Plandetails',
    currentPlan: 'Huidig plan',
    perMonth: 'maand',
    testVerifications: 'Testverificaties',
    unlimited: 'Onbeperkt',
    liveVerifications: 'Live-verificaties',
    maxTestKeys: 'Max testsleutels',
    maxLiveKeys: 'Max live-sleutels',
    suspendTitle: 'Dit account schorsen?',
    suspendDescription:
      'Geschorste gebruikers kunnen niet inloggen of nieuwe verificatiesessies starten. Bestaande data blijft bewaard.',
    suspend: 'Schorsen',
    reactivateTitle: 'Dit account heractiveren?',
    reactivateDescription: 'Heractiveren herstelt toegang direct.',
    reactivate: 'Heractiveren',
    totalSessions: 'Totaal sessies',
    completed: 'Voltooid',
    failed: 'Mislukt',
    thisMonth: 'Deze maand',
    apiKeys: 'API-sleutels',
    totalKeys: 'Totaal sleutels',
    activeKeys: 'Actieve sleutels',
    publicId: 'Publieke ID',
    environment: 'Omgeving',
    lastUsed: 'Laatst gebruikt',
    live: 'Live',
    test: 'Test',
    active: 'Actief',
    revoked: 'Ingetrokken',
    never: 'Nooit',
    showingRange: '{start} t/m {end} van {total} sleutels',
    previous: 'Vorige',
    next: 'Volgende',
    pageOf: 'Pagina {page} van {total}',
  },
};

const nl: AdminUiCopy = {
  ...en,
  publicHeader: {
    features: 'Functies',
    howItWorks: 'Hoe het werkt',
    pricing: 'Prijzen',
    blog: 'Blog',
    docs: 'Docs',
    signIn: 'Inloggen',
    getApiKey: 'API Key ophalen',
  },
  login: {
    ...en.login,
    welcomeBack: 'Welkom terug',
    subtitle: 'Log in op je dashboard',
    signIn: 'Inloggen',
    noAccount: 'Nog geen account?',
    createAccount: 'Account maken',
  },
  register: {
    ...en.register,
    createAccount: 'Account maken',
    subtitle: 'Start met een gratis plan - geen creditcard nodig',
    fullName: 'Volledige naam',
    companyOptional: 'Bedrijf (optioneel)',
    passwordStrength: 'Wachtwoordsterkte',
    creating: 'Account aanmaken…',
    haveAccount: 'Heb je al een account?',
    signIn: 'Inloggen',
    teamInviteAcceptedTitle: 'Teamuitnodiging geaccepteerd',
    teamInviteAcceptedBody: 'Je bent toegevoegd aan het team. Doorsturen naar je teamdashboard...',
    inviteLockedEmailHint: 'Dit e-mailadres is gekoppeld aan je teamuitnodiging en kan niet worden gewijzigd.',
  },
  forgotPassword: {
    ...en.forgotPassword,
    checkEmail: 'Controleer je e-mail',
    accountExistsNoticeSuffix: 'ontvang je binnenkort een link om je wachtwoord te resetten.',
    title: 'Wachtwoord vergeten?',
    sendResetLink: 'Reset-link versturen',
    rememberPassword: 'Weet je je wachtwoord weer?',
    signIn: 'Inloggen',
  },
  resetPassword: {
    ...en.resetPassword,
    successTitle: 'Wachtwoord gereset',
    successBody: 'Je wachtwoord is succesvol gereset.',
    successCta: 'Nu inloggen',
    title: 'Wachtwoord resetten',
    newPassword: 'Nieuw wachtwoord',
    confirmPassword: 'Wachtwoord bevestigen',
    reset: 'Wachtwoord resetten',
    rememberPassword: 'Weet je je wachtwoord weer?',
    signIn: 'Inloggen',
  },
  notFound: {
    pageNotFound: 'Pagina niet gevonden',
    description: 'De pagina die je zoekt bestaat niet of is verplaatst.',
    goBack: 'Terug',
    goDashboard: 'Naar dashboard',
    supportPrefix: 'Als je denkt dat dit een fout is, neem contact op met',
  },
  teamManagement: {
    title: 'Team',
    subtitleWithCount: '{current} van {max} leden',
    subtitleDefault: 'Beheer je teamleden',
    leaveTeam: 'Team verlaten',
    planLockedTitle: 'Teambeheer is beschikbaar op betaalde abonnementen',
    planLockedBody: 'Upgrade naar Pro voor maximaal 3 teamleden, of Business voor maximaal 10.',
    viewPlans: 'Abonnementen bekijken',
    inviteMemberTitle: 'Teamlid uitnodigen',
    inviteCopiedTitle: 'Uitnodigingslink gekopieerd',
    copyAgain: 'Opnieuw kopiëren',
    inviteLinkHint: 'Deel deze link met je teamlid. Verloopt over 7 dagen.',
    invitePlaceholder: 'collega@bedrijf.nl',
    roleAdmin: 'Admin',
    roleDeveloper: 'Ontwikkelaar',
    roleViewer: 'Lezer',
    roleAdminDescription: 'Volledige toegang',
    roleDeveloperDescription: 'Sleutels en playground beheren',
    roleViewerDescription: 'Alleen-lezen toegang',
    sendInvite: 'Uitnodiging verzenden',
    sendingInvite: 'Verzenden...',
    limitReachedPrefix: 'Ledenlimiet bereikt.',
    upgradePlan: 'Abonnement upgraden',
    membersTitle: 'Leden',
    owner: 'Eigenaar',
    ownerDescription: 'Accounteigenaar',
    joinedOn: 'Lid sinds {date}',
    pending: 'In afwachting',
    remove: 'Verwijderen',
    noMembers: 'Nog geen teamleden.',
    noMembersCta: 'Stuur een uitnodiging om te starten.',
    pendingInvitesTitle: 'Openstaande uitnodigingen',
    expires: 'Verloopt op {date}',
    copyLink: 'Link kopiëren',
    revoke: 'Intrekken',
    leaveDialogTitle: 'Team verlaten',
    leaveDialogBody:
      'Weet je zeker dat je dit team wilt verlaten? Je verliest toegang tot deze accountresources en hebt een nieuwe uitnodiging nodig om terug te keren.',
    leaveDialogConfirm: 'Team verlaten',
    removeDialogTitle: 'Teamlid verwijderen',
    removeDialogBody: '{email} uit het team verwijderen? Toegang wordt direct ingetrokken.',
    removeDialogConfirm: 'Verwijderen',
    revokeDialogTitle: 'Uitnodiging intrekken?',
    revokeDialogBody: 'De uitnodiging aan {email} wordt geannuleerd. Deze persoon kan niet toetreden tot het team.',
    toastInviteCopied: 'Uitnodigingslink gekopieerd',
    toastCopiedAgain: 'Opnieuw gekopieerd',
    toastInviteRevoked: 'Uitnodiging ingetrokken',
    toastInviteRevokeFailed: 'Uitnodiging intrekken mislukt',
    toastMemberRemoved: 'Teamlid verwijderd',
    toastMemberRemoveFailed: 'Teamlid verwijderen mislukt',
    toastRoleUpdated: 'Rol bijgewerkt',
    toastRoleUpdateFailed: 'Rol bijwerken mislukt',
    toastLeftTeam: 'Je hebt het team verlaten',
    toastLeaveTeamFailed: 'Team verlaten mislukt',
    inviteSendFailed: 'Uitnodiging verzenden mislukt',
  },
  auditLog: {
    title: 'Auditlog',
    subtitle: 'Volg alle activiteit op je account.',
    searchPlaceholder: 'Zoek op actie, actor, doel, IP, request-ID of event-ID',
    allCategories: 'Alle categorieën',
    categorySecurity: 'Beveiliging',
    categoryApi: 'API',
    categoryDataAccess: 'Datatoegang',
    categoryConfiguration: 'Configuratie',
    allSeverities: 'Alle ernstniveaus',
    severityInfo: 'Info',
    severityWarning: 'Waarschuwing',
    severityError: 'Fout',
    severityCritical: 'Kritiek',
    clearFilters: 'Filters wissen',
    loading: 'Auditlogs laden...',
    empty: 'Geen auditlog-items gevonden.',
    noMatches: 'Geen overeenkomende items op deze pagina.',
    timestamp: 'Tijdstip',
    eventKey: 'Event-sleutel',
    action: 'Actie',
    actor: 'Actor',
    target: 'Doel',
    category: 'Categorie',
    severity: 'Ernst',
    source: 'Bron',
    ipAddress: 'IP-adres',
    eventId: 'Event-ID',
    requestId: 'Request-ID',
    userAgent: 'User-agent',
    unknown: 'Onbekend',
    system: 'Systeem',
    pageSummary: 'Pagina {page} van {totalPages} ({total} items)',
    previous: 'Vorige',
    next: 'Volgende',
    tapToExpand: 'Tik voor details',
    details: 'Details',
  },
  inviteAccept: {
    loading: 'Uitnodigingsdetails laden...',
    noToken: 'Geen uitnodigingstoken opgegeven.',
    invalidInviteDefault: 'Deze uitnodiging is ongeldig, verlopen of al gebruikt.',
    acceptFailedDefault: 'Uitnodiging accepteren mislukt. Mogelijk verlopen of ingetrokken.',
    inviteTitle: 'Je bent uitgenodigd',
    inviteSubtitleNeedsLogin: 'Log in of maak een account om deel te nemen',
    organization: 'Organisatie',
    invitedBy: 'Uitgenodigd door',
    yourRole: 'Jouw rol',
    role: 'Rol',
    acceptInvite: 'Uitnodiging accepteren',
    joiningTeam: 'Je wordt toegevoegd aan het team...',
    successTitle: 'Gelukt',
    successBodyWithOrg: 'Je bent toegevoegd aan {organization}. Doorsturen...',
    successBodyDefault: 'Je bent toegevoegd aan het team. Doorsturen...',
    errorTitle: 'Uitnodiging kon niet worden geaccepteerd',
    goLogin: 'Naar inloggen',
    signInToAccept: 'Inloggen om te accepteren',
    createAccount: 'Account maken',
    signIn: 'Inloggen',
    needsRegisterTitle: 'Maak je account',
    needsRegisterBodyWithOrg: 'Maak een account met {email} om deel te nemen aan {organization}.',
    needsRegisterBodyDefault: 'Maak een account met {email} om deel te nemen aan het team.',
    roleAdmin: 'Admin',
    roleDeveloper: 'Ontwikkelaar',
    roleViewer: 'Lezer',
    roleAdminDescription: 'Volledige toegang tot sleutels, webhooks, facturatie en teambeheer',
    roleDeveloperDescription: 'API-sleutels maken en beheren, playground gebruiken',
    roleViewerDescription: 'Alleen-lezen toegang tot facturatie en auditlog',
  },
  keys: {
    ...en.keys,
    title: 'API-sleutels',
    createKey: '+ Sleutel maken',
    emptyAll: 'Nog geen API-sleutels.',
  },
  webhooks: {
    ...en.webhooks,
    title: 'Webhooks',
    createTitle: 'Webhook maken',
    createWebhook: 'Webhook maken',
    existingTitle: 'Bestaande webhooks',
    inspectorTitle: 'Webhook-inspector',
  },
  billing: {
    ...en.billing,
    title: 'Abonnementen en facturatie',
    manageBilling: 'Facturatie beheren',
    currentPlan: 'Huidig abonnement',
    freeFeatures: [
      'Onbeperkte testverificaties',
      '100 live verificaties/maand',
      '5 testsleutels, 2 live sleutels',
      'Basis-sandbox (geslaagd/mislukt)',
      'Integratiesnippets (10 talen)',
      'E-mailondersteuning',
    ],
    proFeatures: [
      'Onbeperkte testverificaties',
      '1.000 live verificaties/maand',
      '10 testsleutels, 5 live sleutels',
      'Tot 3 teamleden',
      "20+ vooraf ingestelde testscenario's",
      'Webhook-inspector',
      '99,9% SLA',
      'E-mailondersteuning',
    ],
    businessFeatures: [
      'Onbeperkte testverificaties',
      '10.000 live verificaties/maand',
      '10 testsleutels, 10 live sleutels',
      'Tot 10 teamleden',
      'Alles uit Pro',
      'Bouw je eigen scenario’s',
      'EUDI readiness-rapport (compliance-export)',
      'Landsimulatie (DE, FR, ES, IT, NL)',
      'Geavanceerde analyses',
      'Prioritaire e-mailondersteuning',
    ],
    billingCycle: 'Facturatiecyclus',
    contactSales: 'Contact verkoop',
  },
  dashboard: {
    ...en.dashboard,
    overview: 'Overzicht',
    noPriorData: 'Geen eerdere data',
    noChange: 'Geen verandering',
    vsLastPeriod: '{value}% t.o.v. vorige periode',
    last24Hours: 'Laatste 24 uur',
    last30Days: 'Laatste 30 dagen',
    last7Days: 'Laatste 7 dagen',
    hour: 'uur',
    day: 'dag',
    totalVerifications: 'Totaal verificaties',
    successRate: 'Slagingspercentage',
    activeSessions: 'Actieve sessies',
    currentlyInProgress: 'Momenteel bezig',
    verificationTrend: 'Verificatietrend',
    trackVerificationVolume: 'Volg het verificatievolume over tijd',
    total: 'Totaal',
    completed: 'Voltooid',
    failed: 'Mislukt',
    average: 'Gemiddelde',
    peak: 'Piek',
    perBucket: 'Per {bucket}',
    avgPerBucket: 'Gem. per {bucket}',
    noDataYet: 'Nog geen data',
    verificationsWillAppear: 'Verificaties verschijnen hier zodra je begint',
    currentPlan: 'Huidig abonnement',
    manage: 'Beheren',
    staff: 'Staff',
    internalAccount: 'Intern account',
    noBillingForStaff: 'Geen facturatie voor staff',
    liveVerificationsThisMonth: 'Live-verificaties (deze maand)',
    upgradeForLiveAccess: 'Upgrade voor live-toegang',
    nextBillDate: 'Volgende factuurdatum',
    status: 'Status',
    daysRemaining: '{days} dagen resterend',
    renewingSoon: 'Verlenging binnenkort',
    gracePeriod: 'Respijtperiode',
    daysToUpdatePayment: '{days} dagen om betaling bij te werken',
    active: 'Actief',
    allSystemsOperational: 'Alle systemen operationeel',
    advancedAnalytics: 'Geavanceerde analyses',
    detailedBreakdown: 'Gedetailleerde uitsplitsing per omgeving en API-sleutel',
    exportCsv: 'CSV exporteren',
    byEnvironment: 'Per omgeving',
    byStatus: 'Per status',
    live: 'Live',
    test: 'Test',
    topApiKeysThisMonth: 'Top API-sleutels (deze maand)',
    advancedAnalyticsUpgrade: 'Geavanceerde analyses. Upgrade voor gedetailleerde uitsplitsingen en CSV-export.',
    upgrade: 'Upgraden',
    developerResources: 'Ontwikkelaarsresources',
    documentation: 'Documentatie',
    typescriptSdk: 'TypeScript-SDK',
    onboardingWelcome: 'Welkom bij WalletGate',
    youreAllSet: 'Alles is klaar!',
    accountReady: 'Je account is gereed. Voltooi je eerste verificatie in minder dan 5 minuten.',
    getStartedIn3Steps: 'Begin in 3 stappen',
    getYourApiKey: 'Haal je API-sleutel op',
    apiKeyReady: 'Je hebt al een API-sleutel klaar voor gebruik',
    createFirstApiKey: 'Maak je eerste API-sleutel aan om verzoeken te authenticeren',
    viewApiKeys: 'API-sleutels bekijken',
    createApiKey: 'API-sleutel maken',
    installSdk: 'SDK installeren',
    addWalletGate: 'Voeg WalletGate toe aan je project met één commando',
    copyCommand: 'Commando kopiëren',
    copied: 'Gekopieerd!',
    makeFirstRequest: 'Doe je eerste verzoek',
    startVerifying: 'Begin met het verifiëren van digitale identiteiten met een paar regels code',
    viewDocumentation: 'Documentatie bekijken',
    quickStartExample: 'Snelstartvoorbeeld',
    copyCode: 'Code kopiëren',
    apiDocumentation: 'API-documentatie',
    apiDocumentationDesc: 'Volledige API-referentie en handleidingen',
    codeExamples: 'Codevoorbeelden',
    codeExamplesDesc: 'Voorbeeldintegraties in meerdere talen',
    support: 'Ondersteuning',
    supportDesc: 'Krijg hulp van ons team',
    community: 'Community',
    communityDesc: 'Sluit je aan bij andere ontwikkelaars',
    complete: 'Voltooid',
    noPriorRange: 'Geen data voor vorige {range}',
    noChangeVsPrior: 'Geen verandering t.o.v. vorige {range}',
    vsPriorRange: '{value}% t.o.v. vorige {range}',
  },
  playground: {
    ...en.playground,
    quickTour: 'Snelle rondleiding',
    quickStart: 'Snelle start',
    onboardingConfigureTitle: 'Controles instellen',
    onboardingConfigureMessage: 'Kies een snelle template of voeg controles toe en start daarna verificatie.',
    onboardingFlowTitle: 'Bekijk de live-flow',
    onboardingFlowMessage: 'Volg elke mijlpaal van handoff tot beslissing.',
    onboardingShipTitle: 'Sneller leveren',
    onboardingShipMessage: 'Exporteer deze run als JSON of kopieer de integratiecode.',
    configureNow: 'Nu configureren',
    checksPass: '{passed}/{total} controles geslaagd',
    runsUsed: '{used} / {total} uitvoeringen gebruikt',
  },
  notifications: {
    title: 'Meldingen',
    subtitle: 'Blijf op de hoogte van accountactiviteit, beveiligingswaarschuwingen en teamupdates.',
    searchPlaceholder: 'Meldingen zoeken...',
    allCategories: 'Alle categorieën',
    categorySecurity: 'Beveiliging',
    categoryBilling: 'Facturatie',
    categoryApi: 'API',
    categoryTeam: 'Team',
    allStatus: 'Alle',
    unreadOnly: 'Ongelezen',
    readOnly: 'Gelezen',
    clearFilters: 'Filters wissen',
    loading: 'Meldingen laden...',
    empty: 'Nog geen meldingen. Activiteit verschijnt hier zodra het plaatsvindt.',
    noMatches: 'Geen meldingen komen overeen met je filters.',
    markRead: 'Als gelezen markeren',
    markAllRead: 'Alles als gelezen markeren',
    delete: 'Verwijderen',
    viewAll: 'Alle meldingen bekijken',
    preferences: 'Voorkeuren',
    preferencesTitle: 'Meldingsvoorkeuren',
    preferencesSubtitle: 'Kies hoe je voor elke categorie op de hoogte wilt worden gebracht.',
    inApp: 'In-App',
    email: 'E-mail',
    savePreferences: 'Voorkeuren opslaan',
    savedPreferences: 'Meldingsvoorkeuren opgeslagen.',
    failedSavePreferences: 'Meldingsvoorkeuren konden niet worden opgeslagen.',
    category: 'Categorie',
    severity: 'Ernst',
    timestamp: 'Tijd',
    pageSummary: 'Pagina {page} van {total}',
    previous: 'Vorige',
    next: 'Volgende',
    bellNoNotifications: 'Geen nieuwe meldingen',
    bellRecentTitle: 'Meldingen',
    severityInfo: 'Info',
    severityWarning: 'Waarschuwing',
    severityError: 'Fout',
    severityCritical: 'Kritiek',
    toastMarkedRead: 'Melding als gelezen gemarkeerd.',
    toastMarkedAllRead: 'Alle meldingen als gelezen gemarkeerd.',
    toastDeleted: 'Melding verwijderd.',
    toastDeleteFailed: 'Melding verwijderen mislukt.',
    toastNewSingle: 'Je hebt 1 nieuwe melding.',
    toastNewMultiple: 'Je hebt {count} nieuwe meldingen.',
  },
  ...nlExtendedSections,
};

export default nl;
